/**
 * One-time localStorage -> DB migration
 *
 * On first run:
 * 1. Checks `hookhouse_db_migrated_v1` flag in localStorage
 * 2. If not set, reads each of 6 localStorage domains
 * 3. POSTs each to POST /api/migrate/localStorage
 * 4. Tracks per-domain success
 * 5. Only sets hookhouse_db_migrated_v1 = 'true' after ALL domains succeed
 * 6. Failed domains retry on next page load
 *
 * stylePreset is skipped — those are seeded by prisma seed and live in src/constants.ts.
 */

import { fetchWithCsrf } from '@utils/fetchWithCsrf';
import { API_BASE } from '@/config/api';

const MIGRATION_FLAG = 'hookhouse_db_migrated_v1';

interface MigrationResult {
  domain: string;
  imported: number;
  skipped: number;
  errors: { index: number; error: string }[];
  success: boolean;
}

/** Map of migration domain name -> localStorage key */
const DOMAIN_KEYS: Record<string, string> = {
  sessionMemory: 'hookhouse_session_memories',
  studioTemplate: 'hookhouse_templates',
  seedHistory: 'hookhouse_riffwriter_history',
  pronunciationFix: 'custom_pronunciation_fixes',
  sharePreset: 'hookhouse_presets',
  riffDna: 'hookhouse_riff_dna',
};

/**
 * Transform raw localStorage data into the shape expected by the migration endpoint.
 * Each domain has slightly different localStorage structures that need normalizing.
 */
function transformDomainData(domain: string, parsed: unknown): unknown[] {
  switch (domain) {
    case 'riffDna': {
      // Single object -> wrap as array snapshot
      const obj = parsed as Record<string, unknown>;
      return [{
        name: 'Migrated config',
        config: obj,
        vibePrompt: (obj.vibePrompt as string) || '',
        archetype: (obj.archetype as string) || 'custom',
        lane: (obj.lane as string) || 'melodic',
      }];
    }

    case 'seedHistory': {
      // Map SongSeed[] to the expected format
      const seeds = Array.isArray(parsed) ? parsed : [];
      return seeds.map((seed: Record<string, unknown>) => ({
        title: `${(seed.genre as string) || 'Unknown'} seed`,
        concept: (seed.vibe as string) || '',
        genre: (seed.genre as string) || 'unknown',
        seed,
      }));
    }

    case 'sessionMemory': {
      const entries = Array.isArray(parsed) ? parsed : [];
      return entries.map((entry: Record<string, unknown>) => ({
        name: entry.name,
        description: entry.description,
        icon: entry.icon || '',
        tags: entry.tags || [],
        snapshot: entry.snapshot || {},
        preview: entry.preview || {},
      }));
    }

    case 'studioTemplate': {
      const templates = Array.isArray(parsed) ? parsed : [];
      return templates.map((t: Record<string, unknown>) => ({
        name: t.name,
        description: (t.description as string) || '',
        tags: t.tags || [],
        config: t.config || {},
      }));
    }

    case 'sharePreset': {
      const presets = Array.isArray(parsed) ? parsed : [];
      return presets.map((p: Record<string, unknown>) => ({
        name: p.name,
        config: p.config || {},
      }));
    }

    case 'pronunciationFix': {
      // Already matches the expected shape: { original, replacement, reason }[]
      return Array.isArray(parsed) ? parsed : [];
    }

    default:
      return Array.isArray(parsed) ? parsed : [];
  }
}

/**
 * Run the one-time localStorage -> DB migration.
 * Safe to call on every mount — exits immediately if the flag is already set
 * AND the DB actually has data. Handles stale flags from prior DB (e.g. SQLite→MariaDB).
 * Completely silent to the user (console logging only).
 */
export async function migrateLocalStorageToDb(): Promise<void> {
  if (localStorage.getItem(MIGRATION_FLAG) === 'true') {
    // Flag is set — verify DB actually has data for at least one domain with localStorage content
    const hasLocalData = Object.values(DOMAIN_KEYS).some(key => {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch { return false; }
    });

    if (hasLocalData) {
      try {
        const res = await fetchWithCsrf('/api/studio-templates/list');
        if (res.ok) {
          const json = await res.json();
          // If the DB is empty but localStorage has data, the flag is stale — re-run
          if (json.count === 0 || (json.data && json.data.length === 0)) {
            console.log('[migration] Stale migration flag detected (DB empty, localStorage has data). Re-running...');
            localStorage.removeItem(MIGRATION_FLAG);
          } else {
            return; // DB has data, migration is genuinely complete
          }
        } else {
          return; // API error, don't re-run
        }
      } catch {
        return; // Network error, don't re-run
      }
    } else {
      return; // No localStorage data to migrate
    }
  }

  console.log('[migration] Starting one-time localStorage -> DB migration...');

  const results: MigrationResult[] = [];
  let allSuccess = true;

  for (const [domain, key] of Object.entries(DOMAIN_KEYS)) {
    const raw = localStorage.getItem(key);
    if (!raw) {
      console.log(`  [migration] ${domain}: no data in localStorage, skipping`);
      results.push({ domain, imported: 0, skipped: 0, errors: [], success: true });
      continue;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      const data = transformDomainData(domain, parsed);

      if (data.length === 0) {
        console.log(`  [migration] ${domain}: empty data, skipping`);
        results.push({ domain, imported: 0, skipped: 0, errors: [], success: true });
        continue;
      }

      const res = await fetchWithCsrf(`${API_BASE}/api/migrate/localStorage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, data, version: 1 }),
      });

      if (res.ok) {
        const result: MigrationResult = await res.json();
        console.log(
          `  [migration] ${domain}: ${result.imported} imported, ${result.skipped} skipped, ${result.errors?.length || 0} errors`,
        );
        results.push(result);
        if (!result.success) allSuccess = false;
      } else {
        console.error(`  [migration] ${domain}: HTTP ${res.status}`);
        allSuccess = false;
        results.push({
          domain,
          imported: 0,
          skipped: 0,
          errors: [{ index: -1, error: `HTTP ${res.status}` }],
          success: false,
        });
      }
    } catch (err) {
      console.error(`  [migration] ${domain}: ${err}`);
      allSuccess = false;
      results.push({
        domain,
        imported: 0,
        skipped: 0,
        errors: [{ index: -1, error: String(err) }],
        success: false,
      });
    }
  }

  if (allSuccess) {
    localStorage.setItem(MIGRATION_FLAG, 'true');
    console.log('[migration] localStorage -> DB migration complete! Flag set.');
  } else {
    console.warn('[migration] Some domains failed. Will retry on next page load.');
  }
}
